import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { useCreateLead, useUpdateLead } from '../../hooks/backend/crm'
import { toast } from '../../lib/shadcn/sonner'

export const STAGES = ['New', 'Working', 'Won', 'Lost'] as const
export type Stage = (typeof STAGES)[number]

export interface Lead {
  id: number
  name: string
  email: string
  phone: string
  notes: string
  stage: string
  converted_customer_id: string | null
  created_at: string
  updated_at: string
  website?: string | null
  facebook_url?: string | null
  instagram_url?: string | null
  twitter_url?: string | null
  linkedin_url?: string | null
  tiktok_url?: string | null
  youtube_url?: string | null
  enrichment_summary?: string | null
  enriched_at?: string | null
  enriched_by?: string | null
}

interface Props {
  open: boolean
  lead: Lead | null
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}

interface FormFields {
  name: string
  email: string
  phone: string
  notes: string
  stage: Stage
}

const EMPTY: FormFields = { name: '', email: '', phone: '', notes: '', stage: 'New' }

export default function LeadFormDialog({ open, lead, onOpenChange, onSaved }: Props) {
  const createLead = useCreateLead()
  const updateLead = useUpdateLead()
  const [form, setForm] = useState<FormFields>(EMPTY)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    if (lead) {
      setForm({
        name: lead.name,
        email: lead.email,
        phone: lead.phone,
        notes: lead.notes,
        stage: (STAGES as readonly string[]).includes(lead.stage) ? (lead.stage as Stage) : 'New',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, lead])

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required')
      return
    }
    setSaving(true)
    try {
      if (lead) {
        await updateLead.trigger({ id: lead.id, ...form })
        toast.success('Lead updated')
      } else {
        await createLead.trigger({ ...form })
        toast.success('Lead added')
      }
      onSaved()
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save lead')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => { if (!saving) onOpenChange(o) }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{lead ? 'Edit Lead' : 'Add Lead'}</DialogTitle>
          <DialogDescription>
            {lead ? 'Update this lead&apos;s details and pipeline stage.' : 'Capture a new lead in the pipeline.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="lead-name">Name</Label>
            <Input id="lead-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Company or contact name" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="lead-email">Email</Label>
              <Input id="lead-email" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="name@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lead-phone">Phone</Label>
              <Input id="lead-phone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1-555-000-0000" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lead-stage">Stage</Label>
            <Select value={form.stage} onValueChange={v => setForm(p => ({ ...p, stage: v as Stage }))}>
              <SelectTrigger id="lead-stage">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STAGES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lead-notes">Notes</Label>
            <Textarea id="lead-notes" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={4} placeholder="Context, next steps, etc." />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {lead ? 'Save Changes' : 'Add Lead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
